import React, { useEffect, useRef, useState, useContext, MouseEvent, useCallback } from 'react';
import { Editor, NodeViewWrapper } from '@tiptap/react';
import classNames from 'classnames';

import DocumentContext from '../../../contexts/DocumentContext.context';
import { useCopyBetweenDocs, useUploadImage } from '../../../api';
import config from '@config/index';
import blobToBase64 from '@modules/documents/helpers/blobToBase64.helper';
import { DOCUMENT_HTML_ATTRIBUTES, DOTS_POSITIONS } from '@modules/documents/constants';
import { TImageNodeAttrs, TImageNodeResizeStart, TImageNodeSize } from '@modules/documents/types';
import { Spinner } from '@fluentui/react-components';
import setNodeAttrsNoHistory from '../../utils/setNodeAttrsNoHistory';
import { isBoundDocument, isSelectionInField } from '../../extensions/TemplateLocking';
import { runPendingEditorOperation } from '../../../helpers/pendingEditorOperations.helper';

type TProps = {
  node: {
    attrs: any;
    nodeSize: number;
  };
  editor: Editor;
  updateAttributes: (attributes: any) => void;
  getPos: () => number | undefined;
};

const Component = React.memo(({ node, editor, updateAttributes, getPos }: TProps) => {
  // --- SILENT UPLOAD:
  const attrs: TImageNodeAttrs = node.attrs;

  const { file, blobName, docId, id } = attrs;
  const { doc, permissions } = useContext(DocumentContext);
  const [base64, setBase64] = useState('');
  const [alt, setAlt] = useState('');

  // cross-doc copy needed?
  const needsCopy = !!blobName && !!docId && docId !== doc._id;

  let src = '';
  if (permissions.token && blobName)
    src = `${config.AZURE_STORAGE_BASE_URL}/${doc._id}/${encodeURIComponent(blobName)}?${permissions.token}`;
  else src = base64;

  const handleOnError = useCallback(() => {
    if (editor.isDestroyed) return;
    const from = getPos();
    if (from === undefined) return;
    const failedNode = editor.state.doc.nodeAt(from);
    const to = from + node.nodeSize;
    editor.commands.deleteRange({ from, to });
    if (failedNode && editor.state.doc.nodeAt(from) === failedNode) {
      throw new Error('The failed image could not be removed from the editor.');
    }
  }, [getPos]);

  const setAttrsNoHistory = useCallback(
    (patch: Partial<TImageNodeAttrs>) => {
      if (editor.isDestroyed) return;
      const pos = getPos();
      if (pos === undefined) return;
      setNodeAttrsNoHistory<TImageNodeAttrs>(editor.view, pos, patch);
    },
    [editor.view, getPos]
  );

  const uploadImage = useUploadImage();
  useEffect(() => {
    // INSPIRED BY: https://gist.github.com/slava-vishnyakov/16076dff1a77ddaca93c4bccd4ec4521?permalink_comment_id=3733208
    // Important: The attributes may change a few milliseconds after the first render. Hence we must listen on changes to the file attribute from `null` -> File
    if (file) {
      blobToBase64(file).then((data) => setBase64(data as string));
      void runPendingEditorOperation(editor, async () => {
        let blobName: string;
        try {
          blobName = await uploadImage.mutateAsync({ documentId: doc._id, file });
        } catch {
          handleOnError();
          return;
        }
        setAttrsNoHistory({ blobName, docId: doc._id, file: null });
      });
    }
  }, [file]);

  // --- cross-doc copy when blob came from another document
  const copyBetweenDocs = useCopyBetweenDocs();
  useEffect(() => {
    if (needsCopy) {
      void runPendingEditorOperation(editor, async () => {
        let newBlobName: string;
        try {
          newBlobName = await copyBetweenDocs.mutateAsync({ fromDocId: docId, toDocId: doc._id, blobName });
        } catch {
          handleOnError();
          return;
        }
        setAttrsNoHistory({ blobName: newBlobName, docId: doc._id });
      });
    }
  }, [needsCopy]);

  // --- RESIZING:

  const [size, setSize] = useState<TImageNodeSize>({
    width: attrs.width || 'auto',
    height: 'auto',
  });
  const [isActive, setIsActive] = useState<boolean>(false);

  const refResizeStart = useRef<TImageNodeResizeStart | null>(null);
  const refContainer = useRef<HTMLDivElement | null>(null);
  const refImage = useRef<HTMLImageElement | null>(null);

  // Image events:
  const handleImageLoad = () => {
    if (!refImage.current) return;
    setSize({
      width: refImage.current.offsetWidth + 'px',
      height: refImage.current.offsetHeight + 'px',
    });

    /*
    const nodeAfter = editor.state.doc.resolve(getPos()+1).nodeAfter;
    if (nodeAfter && nodeAfter.type.name === 'paragraph') {
      console.log("hello")
    }
    */
  };
  const handleImageClick = () => {
    if (!editor.isEditable) return;
    if (isBoundDocument(editor.state) && !isSelectionInField(editor.state)) return;
    if (!refImage.current) return;
    setIsActive(true);
  };

  // Track whether we're currently resizing (to attach/detach document listeners on demand)
  const [isResizing, setIsResizing] = useState(false);

  // Dot events - starts resize operation:
  const getHandleDotMouseDown = (dotIndex: number) => (e: MouseEvent<HTMLImageElement>) => {
    e.preventDefault();
    if (!isActive) return;
    if (!refImage.current) return;
    refResizeStart.current = {
      x: e.clientX,
      width: refImage.current.offsetWidth,
      height: refImage.current.offsetHeight,
      dotIndex,
    };
    setIsResizing(true);
  };

  // Document event handlers (only attached during active resize):
  const handleDocumentMouseMove = useCallback((e: globalThis.MouseEvent) => {
    if (!refResizeStart.current) return;

    const { x: startX, width: startWidth, height: startHeight, dotIndex } = refResizeStart.current;
    const deltaX = e.clientX - startX;

    const newWidth = startWidth + deltaX * (dotIndex < 2 ? -1 : 1);
    const aspectRatio = startWidth / startHeight;
    const newHeight = newWidth / aspectRatio;

    setSize({ width: newWidth + 'px', height: newHeight + 'px' });
  }, []);

  const handleDocumentMouseUp = useCallback(() => {
    if (!refResizeStart.current) return;
    if (!refImage.current) return;
    refResizeStart.current = null;
    updateAttributes({ width: refImage.current.offsetWidth + 'px' });
    setIsResizing(false);
  }, [updateAttributes]);

  // Attach mousemove/mouseup listeners ONLY while resizing (event delegation pattern)
  useEffect(() => {
    if (!isResizing) return;

    document.addEventListener('mousemove', handleDocumentMouseMove);
    document.addEventListener('mouseup', handleDocumentMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleDocumentMouseMove);
      document.removeEventListener('mouseup', handleDocumentMouseUp);
    };
  }, [isResizing, handleDocumentMouseMove, handleDocumentMouseUp]);

  // Click-outside to deactivate (single listener per image, but only fires on mousedown)
  useEffect(() => {
    const handleDocumentMouseDown = (e: globalThis.MouseEvent) => {
      if (!refContainer.current) return;
      if (refContainer.current.contains(e.target as Node)) return;
      setIsActive(false);
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
  }, []);

  // Delayed alt text fallback
  useEffect(() => {
    const timeoutId = setTimeout(() => setAlt(attrs.alt || 'Cannot load image!'), 1300);
    return () => clearTimeout(timeoutId);
  }, [attrs.alt]);

  useEffect(() => {
    const width = attrs.width || 'auto';
    if (size.width != width) setSize({ ...size, width });
  }, [attrs.width]);

  const extraProps = {
    [DOCUMENT_HTML_ATTRIBUTES.CUSTOM_UNIQUE_ID]: id, // Custom unique ID for the image node; used for scrolling to the image
  };

  return (
    <NodeViewWrapper style={{ textAlign: attrs.textAlign }}>
      {needsCopy ? (
        <div className="flex justify-start items-center gap-1 flex-nowrap text-nowrap">
          <Spinner size="extra-tiny" />
          <span>Copying image...</span>
        </div>
      ) : (
        <div
          {...extraProps}
          ref={refContainer}
          className={classNames('relative cursor-pointer outline-2 max-w-full h-auto inline-block min-w-3', {
            'outline-gray-700 outline-dashed': isActive, // outline-dashed must be placed here, otherwise the gapcursor becomes dashed too
            'outline-white': !isActive,
            'image-wrap': attrs.wrap,
          })}
          style={{ width: size.width }}
        >
          <img
            draggable
            data-drag-handle
            src={src}
            alt={alt}
            onLoad={handleImageLoad}
            onClick={handleImageClick}
            ref={refImage}
            className="max-w-full h-auto inline-block min-w-3"
            style={{
              width: size.width,
              maxHeight: size.width == 'auto' ? '400px' : 'none' /* 400px also needs to be changed in content.scss */,
            }}
          />

          {DOTS_POSITIONS.map((className: string, idx: number) => (
            <div
              key={idx}
              onMouseDown={getHandleDotMouseDown(idx)}
              className={classNames('absolute w-3 h-3 rounded-full', className, {
                'border-2 border-black bg-white': isActive,
              })}
            />
          ))}
        </div>
      )}
    </NodeViewWrapper>
  );
});

export default Component;
